import type {Ref} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';
import {Plus, UserPlus} from 'lucide-react';
import {ActionRow, Button} from '@chekinapp/ui';
import {GUEST_FORM_NAMES, type Participant} from '@chekin/core';
import {InstantCheckInFormTypes as FormTypes, type OnboardingState} from '@chekin/shared';
import {useContracts, useGuestGroup} from 'hooks';
import {useFetchParticipants} from 'hooks/auth/useFetchParticipants';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {isAdult} from 'utils/common';
import {buildStateFormData} from 'pages/Auth/utils';
import {useValidateInstantChekinFlow} from 'pages/Auth/hooks/useValidateInstantChekinFlow';
import {SelectGuestList} from 'pages/Auth/components/SelectGuestList';
import {AgreementClausesBox} from 'components/AgreementClausesBox';
import {ContractBox} from 'components/SignBlock/ContractBox';
import {PropertyLinkShell} from 'pages/SearchReservation/components/PropertyLinkShell';
import {SignedInAs, StepHeading} from './StepParts';

export type RegisterRequest = {
  participants: Participant[];
  guestsData: OnboardingState['guestsData'];
  leaderId?: string;
};

type Props = {
  columnRef: Ref<HTMLDivElement>;
  onRegister: (request: RegisterRequest) => void;
  onAddGuest: () => void;
  onLater: () => void;
};

function PickStep({columnRef, onRegister, onAddGuest, onLater}: Props) {
  const {t} = useTranslation();
  const {
    data: participants,
    isLoading: isLoadingParticipants,
    isError: isParticipantsError,
    refetch: refetchParticipants,
  } = useFetchParticipants();
  const {data: reservation} = useReservation();
  const {agreementClauses: clauses, formFields} = useSchema();
  const {guestGroup, leaderGuest} = useGuestGroup();
  const {contracts, isContractsError, isLoadingContracts} = useContracts();
  const {validateFlow} = useValidateInstantChekinFlow();

  const formMethods = useForm<FormTypes>({defaultValues: {participants: []}});
  const {handleSubmit, control, setError, formState} = formMethods;
  const selected = useWatch({control, name: 'participants'}) ?? [];

  const knownGuests = guestGroup?.known_number_of_guests ?? 0;
  const remaining = Math.max(0, knownGuests - (guestGroup?.added_number_of_guests ?? 0));
  const leaderId = leaderGuest
    ? undefined
    : selected.find(participant => isAdult(participant.birth_date))?.id;
  const leftAfterSelection = Math.max(0, remaining - selected.length);

  const handleConfirm = (data: FormTypes) => {
    const chosen = data.participants ?? [];
    const {isValid, error: validationError} = validateFlow(chosen);
    if (!isValid) {
      setError('participants', {message: validationError ?? undefined});
      return;
    }

    const {guestsData} = buildStateFormData(chosen, clauses, data);
    onRegister({participants: chosen, guestsData, leaderId});
  };

  if (isLoadingParticipants) {
    return <PropertyLinkShell columnRef={columnRef} skeleton />;
  }

  if (isParticipantsError) {
    return (
      <PropertyLinkShell
        columnRef={columnRef}
        dock={
          <>
            <Button className="w-full" onClick={() => void refetchParticipants()}>
              {t('try_again')}
            </Button>
            <Button variant="ghost" className="w-full" onClick={onAddGuest}>
              {t('instant_check_in.fill_form_instead')}
            </Button>
          </>
        }
      >
        <SignedInAs />
        <StepHeading lead={t('instant_check_in.saved_guests_error_lead')}>
          {t('instant_check_in.saved_guests_error')}
        </StepHeading>
      </PropertyLinkShell>
    );
  }

  if (!participants?.length) {
    return (
      <PropertyLinkShell
        columnRef={columnRef}
        dock={
          <>
            <Button className="w-full" onClick={onAddGuest}>
              <UserPlus size={18} />
              {t('instant_check_in.add_first_guest')}
            </Button>
            <Button variant="ghost" className="w-full" onClick={onLater}>
              {t('instant_check_in.do_it_later')}
            </Button>
          </>
        }
      >
        <SignedInAs />
        <StepHeading lead={t('instant_check_in.first_check_in_lead')}>
          {t('instant_check_in.youre_signed_in')}
        </StepHeading>
      </PropertyLinkShell>
    );
  }

  const fieldError = formState.errors.participants?.message;

  return (
    <PropertyLinkShell
      columnRef={columnRef}
      dock={
        <>
          {selected.length === 0 ? (
            <Button variant="link" className="h-auto self-center p-0" onClick={onLater}>
              {t('instant_check_in.add_guests_later')}
            </Button>
          ) : (
            <p
              aria-hidden={leftAfterSelection === 0}
              className="min-h-5 text-center text-[13px] text-[color:var(--chekin-text-secondary)]"
            >
              {leftAfterSelection > 0 &&
                t('instant_check_in.add_others_later', {count: leftAfterSelection})}
            </p>
          )}
          <Button
            className="w-full"
            disabled={selected.length === 0}
            onClick={handleSubmit(handleConfirm)}
          >
            {selected.length
              ? t('instant_check_in.check_in_guests', {count: selected.length})
              : t('instant_check_in.choose_whos_coming')}
          </Button>
        </>
      }
    >
      <SignedInAs />
      <StepHeading lead={t('instant_check_in.pick_lead')}>
        {t('who_is_coming')}
      </StepHeading>

      <FormProvider {...formMethods}>
        <Controller
          name="participants"
          control={control}
          render={({field: {value, ...field}}) => (
            <SelectGuestList
              participants={participants}
              guests={guestGroup?.members ?? []}
              value={value || []}
              leadId={leaderId}
              {...field}
            />
          )}
        />

        <ActionRow
          onClick={onAddGuest}
          leading={
            <span className="grid size-[38px] shrink-0 place-items-center rounded-full bg-[var(--chekin-blue-surface-75)] text-[var(--chekin-color-brand-blue)]">
              <Plus size={18} />
            </span>
          }
          title={t('instant_check_in.add_someone_new')}
          description={t('instant_check_in.not_in_list')}
          className="mt-1 rounded-[14px] border-0 bg-transparent px-[18px] shadow-none hover:bg-[var(--chekin-blue-surface-75)]"
        />

        {fieldError && (
          <p
            role="alert"
            className="mt-3 text-[13px] font-medium text-[var(--chekin-danger)]"
          >
            {fieldError}
          </p>
        )}

        {!!clauses?.length && <AgreementClausesBox clauses={clauses} />}
        <ContractBox
          contracts={contracts}
          hasError={isContractsError}
          isLoading={isLoadingContracts}
          isAutomatedSignature={
            formFields?.[GUEST_FORM_NAMES.signature] && reservation?.auto_signature
          }
        />
      </FormProvider>
    </PropertyLinkShell>
  );
}

export {PickStep};
